// hooks/usePackageSearch.js - Complete Package Search Hook

import { useCallback, useEffect, useRef, useState } from "react";
import { getPackageFilterDefaults } from "../context/envDefaults";
import packageApiService from "../services/packageApiService";

export const usePackageSearch = (initialFilters = {}) => {
	// Filter state
	const defaultFilters = getPackageFilterDefaults();

	const [filters, setFilters] = useState({
		...defaultFilters,
		...initialFilters,
	});

	const [expandedSections, setExpandedSections] = useState({
		// Package Categories
		packageCategoryL1: true,
		packageCategoryL2: true,
		packageCategoryL3: true,
		primaryVertical: true,

		// Targeting & Location
		country: true,
		adPlacement: true,
		deviceTypes: true,
		distributionChannel: true,
		urlTargetingType: true,

		// Deal Information
		dealName: true,
		dealId: true,
		dealSource: true,
		dealStatus: true,
		accountId: true,
		publisherAccountNk: true,
		associatedAdvertisers: true,

		// Audience Information
		audienceId: true,
		audienceName: true,
		audienceProviderName: true,
	});

	// Search state
	const [searchQuery, setSearchQuery] = useState("");
	const [searchTerms, setSearchTerms] = useState([]);
	const [excludeTerms, setExcludeTerms] = useState([]);
	const [isSearching, setIsSearching] = useState(false);
	const [searchResults, setSearchResults] = useState(null);
	const [searchError, setSearchError] = useState(null);
	const [hasSearched, setHasSearched] = useState(false);

	// Filter options state
	const [filterOptions, setFilterOptions] = useState(null);
	const [isLoadingFilters, setIsLoadingFilters] = useState(false);
	const [filterError, setFilterError] = useState(null);

	// Abort controllers
	const searchAbortRef = useRef(null);
	const filterAbortRef = useRef(null);

	// Load filter options
	const loadFilterOptions = useCallback(async (force = false) => {
		try {
			if (filterAbortRef.current) {
				filterAbortRef.current.abort();
			}

			filterAbortRef.current = new AbortController();
			setIsLoadingFilters(true);
			setFilterError(null);

			console.log("Loading package filter options");

			const response = await packageApiService.getPackageFilterOptions(
				filterAbortRef.current.signal,
			);

			if (response.success) {
				setFilterOptions(response.data);
				console.log("Package filter options loaded successfully", {
					cached: response.cached,
					dataKeys: response.data ? Object.keys(response.data) : [],
				});
			} else {
				throw new Error(
					response.error || "Failed to load package filter options",
				);
			}
		} catch (error) {
			if (error.name === "AbortError") {
				console.log("Package filter options request was cancelled");
				return;
			}

			console.error("Failed to load package filter options:", error);
			setFilterError(error.message);
		} finally {
			setIsLoadingFilters(false);
			filterAbortRef.current = null;
		}
	}, []);

	// Search handlers
	const onSearchInputChange = useCallback((e) => {
		setSearchQuery(e.target.value);
	}, []);

	const addSearchTerm = useCallback((term, options = {}) => {
		const { threshold, originalScore, type = "include" } = options;

		const newTerm = {
			query: term,
			text: term,
			threshold: threshold || 0.75,
			originalScore: originalScore || 75,
			id: Date.now(),
		};

		if (type === "include") {
			setSearchTerms((prev) => {
				if (prev.some((t) => t.query === term)) return prev;
				return [...prev, newTerm];
			});
		} else {
			setExcludeTerms((prev) => {
				if (prev.some((t) => t.query === term)) return prev;
				return [...prev, newTerm];
			});
		}

		setSearchQuery("");
	}, []);

	const removeSearchTerm = useCallback((termQuery) => {
		setSearchTerms((prev) => prev.filter((term) => term.query !== termQuery));
		setExcludeTerms((prev) => prev.filter((term) => term.query !== termQuery));
	}, []);

	const clearSearchTerms = useCallback(() => {
		setSearchTerms([]);
		setExcludeTerms([]);
		setSearchQuery("");
	}, []);

	// Main search function
	const triggerSearch = useCallback(
		async (searchOptions = {}) => {
			try {
				if (searchAbortRef.current) {
					searchAbortRef.current.abort();
				}

				searchAbortRef.current = new AbortController();
				setIsSearching(true);
				setSearchError(null);
				setSearchResults(null);

				console.log("Triggering package search with:", {
					searchTerms: searchTerms.length,
					excludeTerms: excludeTerms.length,
					filtersActive: Object.keys(filters).filter((key) => {
						const value = filters[key];
						return (
							value !== null &&
							value !== undefined &&
							(Array.isArray(value) ? value.length > 0 : value !== "")
						);
					}).length,
				});

				const searchParams = {
					searchTerms,
					excludeTerms,
					filters,
					sortBy: searchOptions.sortBy || null,
					view: searchOptions.view || "DESC",
					startIndex: searchOptions.startIndex || 0,
					noOfResult: searchOptions.noOfResult || filters.resultLimit || 100,
					noOfDays: searchOptions.noOfDays || null,
					signal: searchAbortRef.current.signal,
				};

				const response = await packageApiService.searchPackages(searchParams);

				if (response.success) {
					setSearchResults(response.data);
					setHasSearched(true);
					console.log("Package search completed successfully", {
						resultCount:
							response.data?.packages?.length ||
							response.data?.results?.length ||
							0,
					});
				} else {
					throw new Error(response.error || "Failed to search packages");
				}

				return response;
			} catch (error) {
				if (error.name === "AbortError") {
					console.log("Package search request was cancelled");
					return { success: false, cancelled: true };
				}

				console.error("Package search failed:", error);
				setSearchError(error.message);
				return { success: false, error: error.message };
			} finally {
				setIsSearching(false);
				searchAbortRef.current = null;
			}
		},
		[searchTerms, excludeTerms, filters],
	);

	// Filter handlers
	const updateFilter = useCallback((key, value) => {
		console.log("Updating package filter:", key, "=", value);
		setFilters((prev) => ({
			...prev,
			[key]: value,
		}));
	}, []);

	const toggleArrayFilter = useCallback((key, value) => {
		console.log("Toggling package array filter:", key, "value:", value);
		setFilters((prev) => {
			const currentArray = prev[key] || [];
			const isSelected = currentArray.includes(value);

			return {
				...prev,
				[key]: isSelected
					? currentArray.filter((item) => item !== value)
					: [...currentArray, value],
			};
		});
	}, []);

	const toggleSection = useCallback((section) => {
		console.log("Toggling package section:", section);
		setExpandedSections((prev) => ({
			...prev,
			[section]: !prev[section],
		}));
	}, []);

	const clearAllFilters = useCallback(() => {
		console.log("🔧 Clearing all package filters to env defaults");
		setFilters(getPackageFilterDefaults());
	}, []);

	// Reset search
	const resetSearch = useCallback(() => {
		if (searchAbortRef.current) {
			searchAbortRef.current.abort();
		}
		setSearchQuery("");
		setSearchTerms([]);
		setExcludeTerms([]);
		setSearchResults(null);
		setSearchError(null);
		setHasSearched(false);
		setIsSearching(false);
	}, []);

	// LLM integration functions
	const injectSearchResults = useCallback((results) => {
		console.log("Injecting package search results:", results);
		setSearchResults(results);
		setHasSearched(true);
	}, []);

	const triggerSearchWithOverride = useCallback(
		async (searchTermsData, overrideResults) => {
			console.log("Package search with override:", {
				searchTermsData,
				overrideResults,
			});

			// Set search terms
			const includeTerms = searchTermsData.filter(
				(term) => term.type === "include",
			);
			const excludeTermsData = searchTermsData.filter(
				(term) => term.type === "exclude",
			);

			setSearchTerms(includeTerms);
			setExcludeTerms(excludeTermsData);

			// Inject results
			setSearchResults(overrideResults);
			setHasSearched(true);

			return { success: true, data: overrideResults };
		},
		[],
	);

	// Computed values
	const activeFilterCount = Object.keys(filters).filter((key) => {
		const value = filters[key];
		if (Array.isArray(value)) return value.length > 0;
		if (typeof value === "boolean") return value !== null;
		if (typeof value === "number") return value > 0;
		if (typeof value === "string") return value.trim().length > 0;
		return false;
	}).length;

	const hasActiveFilters = activeFilterCount > 0;
	const hasResults =
		searchResults &&
		(searchResults.packages?.length > 0 || searchResults.results?.length > 0);
	const hasError = !!searchError;
	const resultsCount =
		searchResults?.packages?.length || searchResults?.results?.length || 0;
	const excludedPackageIds = searchResults?.excludedPackageIds || [];
	const excludedCount = excludedPackageIds.length;
	const hasExcludedResults = excludedCount > 0;

	// Auto-load filter options
	useEffect(() => {
		loadFilterOptions();

		return () => {
			if (filterAbortRef.current) filterAbortRef.current.abort();
			if (searchAbortRef.current) searchAbortRef.current.abort();
		};
	}, [loadFilterOptions]);

	return {
		// Filter state and handlers
		filters,
		expandedSections,
		filterOptions,
		isLoadingFilters,
		filterError,
		updateFilter,
		toggleArrayFilter,
		toggleSection,
		clearAllFilters,
		loadFilterOptions,

		// Search state and handlers
		searchQuery,
		searchTerms,
		excludeTerms,
		isSearching,
		searchResults,
		searchError,
		hasSearched,
		onSearchInputChange,
		addSearchTerm,
		removeSearchTerm,
		clearSearchTerms,
		triggerSearch,
		resetSearch,

		// LLM integration
		injectSearchResults,
		triggerSearchWithOverride,

		// Computed values
		activeFilterCount,
		hasActiveFilters,
		hasResults,
		hasError,
		resultsCount,
		excludedPackageIds,
		excludedCount,
		hasExcludedResults,
	};
};
